import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchAvimarkSettings, saveAvimarkSettings, type AvimarkExportSettings } from "@common/lib/api/avimarkEditor";
import { useAccountContext } from "@/state/providers/AccountProvider";

export const avimarkSettingsKey = (accountId: string | undefined) => ["avimark-settings", accountId ?? ""] as const;

/** EN-1249: the hub's two body-system settings. Saves on change, optimistic, reverts on failure. */
export function useAvimarkSettings(enabled: boolean) {
    const { accountData } = useAccountContext();
    const qc = useQueryClient();
    const key = avimarkSettingsKey(accountData?.id);
    const [savedAt, setSavedAt] = useState<number | null>(null);

    const query = useQuery({ queryKey: key, queryFn: fetchAvimarkSettings, enabled: enabled && !!accountData?.id });

    const mutation = useMutation({
        mutationFn: saveAvimarkSettings,
        onMutate: async (next: AvimarkExportSettings) => {
            await qc.cancelQueries({ queryKey: key });
            const prev = qc.getQueryData<AvimarkExportSettings>(key);
            qc.setQueryData(key, next);
            setSavedAt(null);
            return { prev };
        },
        onError: (_e, _next, ctx) => {
            if (ctx?.prev) qc.setQueryData(key, ctx.prev);
        },
        onSuccess: (stored) => {
            qc.setQueryData(key, stored);
            setSavedAt(Date.now());
            // The editor's preview renders with these settings.
            void qc.invalidateQueries({ queryKey: ["avimark-preview"] });
        },
    });

    return {
        settings: query.data,
        isLoading: query.isLoading,
        isError: query.isError,
        refetch: () => void query.refetch(),
        save: (next: AvimarkExportSettings) => mutation.mutate(next),
        isSaving: mutation.isPending,
        saveError: mutation.isError,
        savedAt,
    };
}
